/**
 * GroupedProjectSelect Component
 * 
 * A custom collapsible dropdown for selecting project-discipline combinations.
 * Groups options by master project number with expandable/collapsible discipline lists.
 * 
 * Features:
 * - Collapsible project groups (click to expand/collapse)
 * - Search filter for projects by number or name
 * - Portal rendering to escape overflow containers
 * - Keyboard navigation (arrows, enter, escape)
 * - Lightweight: event listeners only active when open
 * 
 * Props:
 * - value: current optionKey string (e.g., "30-PFS") or null
 * - onChange(optionKey): callback when a discipline is selected
 * - options: array of { project_id, job_number, job_name, discipline_code, display_value, optionKey }
 * - placeholder: default "Select project..."
 * - disabled: boolean
 * - style: optional style override for the trigger container
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';

const GroupedProjectSelect = ({
  value = null,
  onChange,
  options = [],
  placeholder = 'Select project...',
  disabled = false,
  style = {},
  onGroupSelect = null  // optional: (jobNumber) => void — called when group header label is clicked
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Find selected option for display
  const selectedOption = useMemo(() => {
    if (!value) return null;
    return options.find(opt => opt.optionKey === value) || null;
  }, [value, options]);

  // Group options by job_number
  const groupedOptions = useMemo(() => {
    const groupMap = new Map();
    for (const opt of options) {
      if (!groupMap.has(opt.job_number)) {
        groupMap.set(opt.job_number, {
          job_number: opt.job_number,
          job_name: opt.job_name || '',
          disciplines: []
        });
      }
      groupMap.get(opt.job_number).disciplines.push(opt);
    }
    // Sort groups by job_number
    const groups = Array.from(groupMap.values());
    groups.sort((a, b) => a.job_number.localeCompare(b.job_number));
    // Sort disciplines within each group
    for (const group of groups) {
      group.disciplines.sort((a, b) => a.display_value.localeCompare(b.display_value));
    }
    return groups;
  }, [options]);

  // Filter groups by search
  const filteredGroups = useMemo(() => {
    if (!searchValue.trim()) return groupedOptions;
    const query = searchValue.toLowerCase().trim();
    return groupedOptions.filter(group =>
      group.job_number.toLowerCase().includes(query) ||
      group.job_name.toLowerCase().includes(query) ||
      group.disciplines.some(d => d.display_value.toLowerCase().includes(query))
    );
  }, [groupedOptions, searchValue]);

  // Auto-expand the group containing the selected value
  useEffect(() => {
    if (selectedOption && !expandedGroups.has(selectedOption.job_number)) {
      setExpandedGroups(prev => {
        const next = new Set(prev);
        next.add(selectedOption.job_number);
        return next;
      });
    }
  }, [selectedOption]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-expand all groups when search is active
  useEffect(() => {
    if (searchValue.trim()) {
      const allNumbers = filteredGroups.map(g => g.job_number);
      setExpandedGroups(new Set(allNumbers));
    }
  }, [searchValue, filteredGroups]);

  // Calculate dropdown position
  const updateDropdownPosition = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom,
        left: rect.left,
        width: Math.max(rect.width, 280)
      });
    }
  }, []);

  // Position updates and scroll/resize listeners only when open
  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
      const handleScrollOrResize = () => updateDropdownPosition();
      window.addEventListener('scroll', handleScrollOrResize, true);
      window.addEventListener('resize', handleScrollOrResize);
      return () => {
        window.removeEventListener('scroll', handleScrollOrResize, true);
        window.removeEventListener('resize', handleScrollOrResize);
      };
    }
  }, [isOpen, updateDropdownPosition]);

  // Focus search input when opening
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 0);
    } else {
      setSearchValue('');
    }
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Toggle group expanded/collapsed
  const toggleGroup = (jobNumber) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(jobNumber)) {
        next.delete(jobNumber);
      } else {
        next.add(jobNumber);
      }
      return next;
    });
  };

  // Handle discipline selection
  const handleSelect = (optionKey) => {
    setIsOpen(false);
    setSearchValue('');
    onChange(optionKey);
  };

  // Handle trigger click
  const handleTriggerClick = () => {
    if (disabled) return;
    if (isOpen) {
      setIsOpen(false);
    } else {
      updateDropdownPosition();
      setIsOpen(true);
    }
  };

  // Handle keyboard in search input
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  // Render the dropdown panel
  const renderDropdown = () => {
    if (!isOpen) return null;

    const dropdownContent = (
      <div
        ref={dropdownRef}
        style={{
          position: 'fixed',
          top: dropdownPosition.top,
          left: dropdownPosition.left,
          width: dropdownPosition.width,
          backgroundColor: 'white',
          border: '1px solid #d1d5db',
          borderRadius: '0 0 6px 6px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
          maxHeight: '320px',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 99999
        }}
      >
        {/* Search input */}
        <div style={{
          padding: '8px',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <input
            ref={searchInputRef}
            type="text"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder="Search projects..."
            style={{
              width: '100%',
              padding: '6px 10px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              fontSize: '0.85rem',
              boxSizing: 'border-box',
              outline: 'none'
            }}
            autoComplete="off"
          />
        </div>

        {/* Scrollable groups area */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          maxHeight: '270px'
        }}>
          {filteredGroups.length === 0 ? (
            <div style={{
              padding: '12px',
              color: '#6b7280',
              fontSize: '0.85rem',
              textAlign: 'center'
            }}>
              No projects found
            </div>
          ) : (
            filteredGroups.map(group => {
              const isExpanded = expandedGroups.has(group.job_number);
              return (
                <div key={group.job_number}>
                  {/* Group header */}
                  <div
                    style={{
                      padding: '8px 12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      backgroundColor: '#f9fafb',
                      borderBottom: '1px solid #e5e7eb',
                      userSelect: 'none'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f3f4f6'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                  >
                    {/* Expand/collapse arrow — only toggles expand */}
                    <span
                      onClick={() => toggleGroup(group.job_number)}
                      style={{
                        fontSize: '0.7rem',
                        color: '#6b7280',
                        width: '16px',
                        textAlign: 'center',
                        flexShrink: 0,
                        transition: 'transform 0.15s ease',
                        transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                        display: 'inline-block',
                        cursor: 'pointer',
                        padding: '2px'
                      }}
                    >
                      &#9654;
                    </span>
                    {/* Project number badge + name — clicking selects the group if onGroupSelect provided */}
                    <span
                      onClick={() => {
                        if (onGroupSelect) {
                          onGroupSelect(group.job_number);
                          setIsOpen(false);
                          setSearchValue('');
                        } else {
                          toggleGroup(group.job_number);
                        }
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        flex: 1,
                        cursor: onGroupSelect ? 'pointer' : 'default',
                        minWidth: 0
                      }}
                    >
                      <span style={{
                        fontSize: '0.85rem',
                        fontWeight: '700',
                        color: onGroupSelect ? '#1d4ed8' : '#1f2937',
                        fontFamily: 'monospace',
                        flexShrink: 0,
                        textDecoration: onGroupSelect ? 'underline' : 'none'
                      }}>
                        {group.job_number}
                      </span>
                      <span style={{
                        fontSize: '0.8rem',
                        color: '#6b7280',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1
                      }}>
                        {group.job_name}
                      </span>
                    </span>
                    {/* Discipline count */}
                    <span style={{
                      fontSize: '0.7rem',
                      color: '#9ca3af',
                      flexShrink: 0
                    }}>
                      {group.disciplines.length}
                    </span>
                  </div>

                  {/* Discipline options (visible when expanded) */}
                  {isExpanded && group.disciplines.map(disc => {
                    const isSelected = disc.optionKey === value;
                    return (
                      <div
                        key={disc.optionKey}
                        onClick={() => handleSelect(disc.optionKey)}
                        style={{
                          padding: '7px 12px 7px 36px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          backgroundColor: isSelected ? '#eff6ff' : 'white',
                          borderBottom: '1px solid #f3f4f6'
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = isSelected ? '#eff6ff' : 'white';
                        }}
                      >
                        {/* Display value */}
                        <span style={{
                          fontSize: '0.85rem',
                          fontFamily: 'monospace',
                          fontWeight: isSelected ? '600' : '400',
                          color: isSelected ? '#2563eb' : '#374151'
                        }}>
                          {disc.display_value}
                        </span>
                        {/* Checkmark for selected */}
                        {isSelected && (
                          <span style={{
                            marginLeft: 'auto',
                            color: '#2563eb',
                            fontSize: '0.85rem',
                            fontWeight: '700'
                          }}>
                            ✓
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>
      </div>
    );

    return ReactDOM.createPortal(dropdownContent, document.body);
  };

  return (
    <div
      ref={triggerRef}
      style={{
        position: 'relative',
        ...style
      }}
    >
      {/* Trigger button */}
      <div
        onClick={handleTriggerClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0.5rem 0.75rem',
          border: '1px solid #d1d5db',
          borderRadius: '6px',
          fontSize: '0.875rem',
          width: '100%',
          minWidth: '160px',
          backgroundColor: disabled ? '#f3f4f6' : 'white',
          cursor: disabled ? 'not-allowed' : 'pointer',
          color: selectedOption ? '#111827' : '#9ca3af',
          boxSizing: 'border-box',
          userSelect: 'none'
        }}
      >
        <span style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {selectedOption ? selectedOption.display_value : placeholder}
        </span>
        <span style={{
          fontSize: '0.6rem',
          color: '#9ca3af',
          marginLeft: '8px',
          flexShrink: 0,
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.15s ease'
        }}>
          ▼
        </span>
      </div>

      {renderDropdown()}
    </div>
  );
};

export default GroupedProjectSelect;
